import {
  Button,
  CreateButton,
  EditButton,
  Icons,
  List,
  notification,
  Popconfirm,
  Space,
  Table,
  TableProps,
  Tag,
} from '@pankod/refine-antd';
import { SearchWithDateRange } from 'components/search/SearchWithDateRange';
import CustomTableWithPagination from 'components/table/custom-table';
import { ManufactureStatus } from 'enums';
import { IQuery, IQueryOptionals } from 'interfaces';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { SearchIndex } from 'services/meilisearch';
import { deleteManufactureProduct, getManufactures } from 'services/product-manufacture';
import MetaData from 'utilities/metadata';
import { getDateRangeOfLastXMonths } from 'utilities/shared';
import styles from './style.module.css';

export const MakeProductList: React.FC = () => {
  const [manufactureList, setManufactureList] = useState<any[]>([]);
  const [totalManufactureItems, setTotalManufactureItems] = useState<number>(0);
  const [dateRange, setDateRange] = useState<{ startDate?: string; endDate?: string }>(() => {
    const [startDate, endDate] = getDateRangeOfLastXMonths(3);
    return {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    };
  });
  const [searchText, setSearchText] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const query = useMemo<IQuery>(() => {
    return {
      startDate: dateRange.startDate ?? '',
      endDate: dateRange.endDate ?? '',
      searchString: searchText,
      start: page,
      limit,
    };
  }, [dateRange.startDate, dateRange.endDate, searchText, page, limit]);

  const fetchManufactureData = useCallback(async (query: IQueryOptionals) => {
    setIsLoading(true);
    try {
      const response = await getManufactures(query);
      setManufactureList(response.data);
      setTotalManufactureItems(response.total);
    } catch (err) {
      if (err instanceof Error) {
        notification.error({
          message: 'Failed',
          description: err.message,
        });
      }
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    fetchManufactureData(query);
  }, [fetchManufactureData, query]);

  const onSearch = async (values: {
    startDate?: string;
    endDate?: string;
    searchString: string;
  }) => {
    setDateRange({ startDate: values.startDate, endDate: values.endDate });
    setSearchText(values.searchString);
    setPage(1);
  };

  const onTableChange: TableProps<any>['onChange'] = async (
    pagination,
    _filters,
    _sorter,
    _extra
  ) => {
    setPage(pagination.current ?? 1);
    setLimit(pagination.pageSize ?? 10);
  };

  const onPageChange = async (page: number, pageSize: number) => {
    setPage(page);
    setLimit(pageSize);
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteManufactureProduct(id);

      // Ensure the current page doesn't go out of bounds
      const newTotalItems = totalManufactureItems - 1;
      const maxPages = Math.ceil(newTotalItems / limit);
      const newPage = page > maxPages ? maxPages : page;

      await fetchManufactureData({
        startDate: query.startDate,
        endDate: query.endDate,
        searchString: query.searchString,
        start: newPage, // Maintain current page
        limit: limit, // Maintain current limit
      });

      setPage(newPage); // Update page state if needed

      notification.success({
        message: 'Success',
        description: 'Deleted successfully',
      });
    } catch (err: any) {
      notification.error({
        message: 'Failed',
        description: err.message,
      });
    }
  };

  const statusStyles: Record<
    ManufactureStatus | string,
    { textColor: string; backgroundColor: string }
  > = {
    [ManufactureStatus['Not Started']]: {
      textColor: '#E53935', // Red for text
      backgroundColor: '#E539350D', // Light red for background
    },
    [ManufactureStatus['In Progress']]: {
      textColor: '#FFA500', // Orange for text
      backgroundColor: '#FFA5001A', // Light orange for background
    },
    [ManufactureStatus['Done']]: {
      textColor: '#43A047', // Green for text
      backgroundColor: '#43A0470D', // Light green for background
    },
  };

  const getStatusStyles = (status: string) => {
    const normalizedStatus = status.trim();
    return statusStyles[normalizedStatus] || { textColor: '#000000', backgroundColor: '#FFFFFF' }; // Fallback to default
  };

  const pageHeaderButtons = <CreateButton type="primary" />;

  const columns = [
    {
      title: 'Manufacture ID',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: 'Product',
      dataIndex: 'product',
      key: 'product',
    },
    {
      title: 'Manufacture Status',
      dataIndex: 'manufactureStatus',
      key: 'manufactureStatus',
      render: (status: string) => {
        const { textColor, backgroundColor } = getStatusStyles(status);
        return (
          <Tag
            style={{
              color: textColor,
              backgroundColor: backgroundColor,
              height: '24px',
              padding: '0px 8px',
              gap: '4px',
              borderRadius: '8px',
              opacity: 1,
              border: 'none',
              fontFamily: 'Nunito Sans',
              fontSize: '14px',
              fontWeight: 400,
              lineHeight: '22px',
            }}
          >
            {status}
          </Tag>
        );
      },
    },
    {
      title: 'Warehouse',
      dataIndex: 'warehouse',
      key: 'warehouse',
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
    },
    {
      title: 'Total Manufacturing Cost',
      dataIndex: 'totalCost',
      key: 'totalCost',
      render: (value: number) => `${value?.toFixed(2)}`,
    },
    {
      title: 'Production Deadline',
      dataIndex: 'productionDeadlineDate',
      key: 'productionDeadlineDate',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_text: any, record: any) => (
        <Space>
          <EditButton size="small" recordItemId={record.id} hideText />
          <Popconfirm
            title="Are you sure?"
            okText="Delete"
            onConfirm={() => handleDelete(record.id)}
          >
            <Button size="small" danger icon={<Icons.DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const tableData = manufactureList.map((item) => {
    const totalProductCost = item.manufactureProducts?.reduce(
      (acc: number, prod: any) => acc + (prod.unitPrice ?? 0) * (prod.quantity ?? 0),
      0
    );

    const totalExpense = item.manufactureExpense?.reduce(
      (acc: number, exp: any) => acc + (exp.amount ?? 0),
      0
    );

    const totalWastageCost = item.manufactureWastage?.reduce(
      (acc: number, waste: any) => acc + (waste.wastageCost ?? 0),
      0
    );

    const totalCost = totalProductCost + totalExpense + totalWastageCost;

    return {
      key: item?.id,
      id: item?.id,
      product: item?.productVariant?.name,
      warehouse: item?.warehouse?.name,
      quantity: item?.quantity,
      productionDeadlineDate: new Date(item?.productionDeadlineDate).toLocaleDateString(),
      manufactureStatus: item?.manufactureStatus,
      totalCost,
    };
  });

  return (
    <List headerProps={{ extra: pageHeaderButtons }}>
      <MetaData />
      <SearchWithDateRange
        placeholder="Search by ID, Warehouse Name, Product, Material"
        size="large"
        className={styles['filter-section__search']}
        index={SearchIndex.Manufacture}
        groups={['id', 'warehouse', 'product', 'material']}
        onSearch={onSearch}
      />
      <CustomTableWithPagination
        total={totalManufactureItems}
        page={page}
        limit={limit}
        onPageChange={onPageChange}
      >
        <Table
          columns={columns}
          loading={isLoading}
          dataSource={tableData}
          pagination={false}
          onChange={onTableChange}
          scroll={{ x: true }}
          rowKey="id"
        />
      </CustomTableWithPagination>
    </List>
  );
};
